import { Link } from "react-router-dom";

const SUPPORT_EMAIL = "km.international19@gmail.com";
const SUPPORT_PHONE = "+91 7302209481";
const WHATSAPP = "917302209481";
const BUSINESS_ADDRESS = "K-Walk, Agra, Uttar Pradesh, India";

function PolicyShell({ eyebrow, title, updated, children, testId }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-20">
        <Link to="/" className="text-xs tracking-[0.3em] uppercase text-[#666] hover:text-black">
          ← Back to K-Walk
        </Link>
        <div className="mt-10 label-eyebrow">{eyebrow}</div>
        <h1 className="font-brand text-4xl md:text-5xl mt-3 leading-tight">{title}</h1>
        <div className="text-xs text-[#666] mt-4 tracking-wider uppercase">Last updated: {updated}</div>
        <div className="mt-10 prose prose-neutral max-w-none text-[15px] leading-[1.85] text-[#222] policy-body">
          {children}
        </div>
      </div>
      <style>{`
        .policy-body h2 { font-family: Georgia, 'Fraunces', serif; font-size: 22px; margin-top: 40px; margin-bottom: 12px; color:#111; }
        .policy-body p, .policy-body li { color:#222; }
        .policy-body ul { list-style: disc; padding-left: 22px; margin: 8px 0 16px 0; }
        .policy-body a { color:#8B5A2B; text-decoration: underline; }
        .policy-body strong { color:#111; }
      `}</style>
    </div>
  );
}

export function Terms() {
  return (
    <PolicyShell eyebrow="Legal" title="Terms of Service" updated="7 February 2026" testId="terms-page">
      <p>
        Welcome to K-Walk. By accessing or purchasing from k-walk.in {'("Site")'}, you agree to be
        bound by the following Terms of Service. Please read them carefully before placing an
        order.
      </p>

      <h2>1. About K-Walk</h2>
      <p>
        K-Walk is a handcrafted footwear label operated from Agra, Uttar Pradesh, India. All
        products are made-to-order or hand-finished by our atelier before dispatch.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        By using the Site you confirm that you are at least 18 years of age or are accessing the
        Site under the supervision of a parent or legal guardian, and that the information you
        provide during checkout is accurate and complete.
      </p>

      <h2>3. Orders &amp; Acceptance</h2>
      <ul>
        <li>All orders are subject to product availability and confirmation of payment.</li>
        <li>K-Walk reserves the right to refuse or cancel any order at its discretion, including for suspected fraud, pricing errors, or stock limitations.</li>
        <li>An order confirmation email is not a guarantee of dispatch. Dispatch is confirmed by a separate shipping email.</li>
      </ul>

      <h2>4. Pricing &amp; Payment</h2>
      <ul>
        <li>All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise.</li>
        <li>Payments are processed securely via Razorpay. We do not store card or UPI details on our servers.</li>
        <li>In the rare event of a pricing error, we will contact you before processing the order.</li>
      </ul>

      <h2>5. Shipping</h2>
      <p>
        Standard dispatch is within 3–5 working days from Agra. Delivery timelines vary by
        pincode and courier. For full details see our Shipping &amp; Returns information.
      </p>

      <h2>6. Returns &amp; Refunds</h2>
      <p>
        Please refer to our <Link to="/refund">Cancellation &amp; Refund Policy</Link> for
        complete details.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        All content on the Site — including logos, product photography, copy, and page design —
        is the property of K-Walk and may not be reproduced without written permission.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        K-Walk is not liable for any indirect, incidental, or consequential damages arising from
        the use of our products or the Site. Our maximum liability in any dispute is limited to
        the value of the specific order in question.
      </p>

      <h2>9. Governing Law</h2>
      <p>
        These Terms shall be governed by the laws of India. Any dispute shall be subject to the
        exclusive jurisdiction of the courts of Agra, Uttar Pradesh.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about these Terms? Email us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or call {SUPPORT_PHONE}.
      </p>
    </PolicyShell>
  );
}

export function Privacy() {
  return (
    <PolicyShell eyebrow="Legal" title="Privacy Policy" updated="7 February 2026" testId="privacy-page">
      <p>
        K-Walk respects your privacy. This policy explains what information we collect, why we
        collect it, and how we protect it.
      </p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li><strong>Contact details:</strong> name, email, phone number, shipping address.</li>
        <li><strong>Order data:</strong> products purchased, sizes, order totals.</li>
        <li><strong>Payment data:</strong> processed directly by Razorpay. We do not store card, UPI, or netbanking credentials.</li>
        <li><strong>Technical data:</strong> anonymous browser and device information used to improve the Site.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To process and fulfil your order.</li>
        <li>To send transactional emails (order confirmation, dispatch, review request).</li>
        <li>To respond to your enquiries and provide customer support.</li>
        <li>To improve our products, service, and website experience.</li>
      </ul>

      <h2>3. Sharing of Information</h2>
      <p>
        We share data only with trusted partners strictly required to fulfil your order — payment
        processors (Razorpay), shipping couriers, and email service providers (Resend). We do
        not sell or rent your personal information to third parties.
      </p>

      <h2>4. Data Retention</h2>
      <p>
        We retain order and contact information for as long as required to service your order,
        respond to warranty enquiries, and comply with tax and regulatory obligations.
      </p>

      <h2>5. Your Rights</h2>
      <p>
        You may request access to, correction of, or deletion of your personal data at any time
        by writing to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We will respond
        within 30 days.
      </p>

      <h2>6. Cookies</h2>
      <p>
        The Site uses only functional cookies necessary for shopping cart and login sessions. We
        do not use third-party advertising cookies.
      </p>

      <h2>7. Security</h2>
      <p>
        We use HTTPS across the Site and store passwords using bcrypt hashing. Payment details
        never touch our servers.
      </p>

      <h2>8. Contact</h2>
      <p>
        For any privacy-related question, write to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </PolicyShell>
  );
}

export function Refund() {
  return (
    <PolicyShell eyebrow="Legal" title="Cancellation, Refund & Shipping Policy" updated="7 February 2026" testId="refund-page">
      <h2>Shipping</h2>
      <ul>
        <li>All orders are dispatched from Agra, Uttar Pradesh within <strong>3–5 working days</strong>.</li>
        <li>Standard delivery within India: 4–7 working days after dispatch, depending on pincode.</li>
        <li>Shipping is complimentary on all orders across India.</li>
        <li>You will receive a shipping confirmation email once your order leaves our workshop.</li>
      </ul>

      <h2>Order Cancellation</h2>
      <ul>
        <li>You may cancel your order for a full refund within <strong>24 hours</strong> of placing it, provided it has not yet entered production.</li>
        <li>Once an order is marked {'"In Production"'} or {'"Shipped"'}, it can no longer be cancelled.</li>
        <li>To cancel, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with your order number.</li>
      </ul>

      <h2>Returns &amp; Exchanges</h2>
      <ul>
        <li>We accept returns and size exchanges within <strong>7 days of delivery</strong>.</li>
        <li>Products must be <strong>unworn, unused, and in original packaging</strong>, with all tags intact. Shoes with any sign of outdoor wear cannot be accepted.</li>
        <li>Wedding, custom, or made-to-order pairs are non-returnable unless there is a manufacturing defect.</li>
        <li>To initiate a return, write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with your order number and reason. We will arrange a reverse pickup where serviceable.</li>
      </ul>

      <h2>Refunds</h2>
      <ul>
        <li>Once your return is received and inspected, we will notify you of approval.</li>
        <li>Approved refunds are processed back to the original payment method within <strong>7–10 working days</strong>.</li>
        <li>Shipping charges (if any) are non-refundable except in the case of a defective or wrong item shipped by us.</li>
      </ul>

      <h2>Damaged or Wrong Item</h2>
      <p>
        If you receive a damaged, defective, or incorrect item, please email a photo to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> within 48 hours of delivery.
        We will arrange a replacement or a full refund, including any shipping charges.
      </p>

      <h2>Contact</h2>
      <p>
        For anything related to your order, reach us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or on WhatsApp at{" "}
        <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer">{SUPPORT_PHONE}</a>.
      </p>
    </PolicyShell>
  );
}

export function Contact() {
  return (
    <PolicyShell eyebrow="Reach Us" title="Contact K-Walk" updated="7 February 2026" testId="contact-page">
      <p>
        We{"'"}re a small team based out of Agra. Whether it{"'"}s a question about sizing, a bespoke
        request, or something that didn{"'"}t go quite right — we{"'"}d love to hear from you.
      </p>

      <h2>Customer Support</h2>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-email-link">{SUPPORT_EMAIL}</a>
        </li>
        <li>
          <strong>Phone:</strong>{" "}
          <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`} data-testid="contact-phone-link">{SUPPORT_PHONE}</a>
        </li>
        <li>
          <strong>WhatsApp:</strong>{" "}
          <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link">Chat on WhatsApp</a>
        </li>
      </ul>

      <h2>Hours</h2>
      <p>Monday – Saturday, 10:00 AM – 7:00 PM IST. We usually reply within one working day.</p>

      <h2>Workshop &amp; Business Address</h2>
      <p>{BUSINESS_ADDRESS}</p>

      <h2>Press &amp; Wholesale</h2>
      <p>
        For editorial, collaborations, or wholesale enquiries, write to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with the subject line {'"Press"'} or{" "}
        {'"Wholesale"'}.
      </p>
    </PolicyShell>
  );
}
